import { motion } from "motion/react";

import { useRevealReducedMotion } from "@/lib/reveal";

interface WaveTextProps {
  text: string;
  className?: string;
  staggerDelay?: number;
  yOffset?: number;
  duration?: number;
  /** Animate whole words instead of characters — calmer, good for body copy. */
  byWord?: boolean;
  /** Hold the cascade for this long — lets split headlines animate in sequence. */
  delay?: number;
}

// Staggered reveal that animates characters inside word-level wrappers, so a
// line can only ever break between words — never mid-word — while keeping the
// per-character cascade on display type.
const WaveText = ({
  text,
  className = "",
  staggerDelay = 0.03,
  yOffset = 10,
  duration = 0.4,
  byWord = false,
  delay = 0,
}: WaveTextProps) => {
  const reducedMotion = useRevealReducedMotion();

  if (reducedMotion) {
    return <span className={className}>{text}</span>;
  }

  const words = text.split(" ").map((word, wordIdx) => ({
    word,
    key: `${word}-${wordIdx}`,
    wordIdx,
  }));

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: byWord ? staggerDelay * 3 : staggerDelay,
        delayChildren: delay,
      },
    },
  };

  const itemVariants = {
    hidden: {
      y: yOffset,
      opacity: 0,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: duration,
        ease: [0.25, 0.1, 0.25, 1] as const,
      },
    },
  };

  return (
    <motion.span
      className={className}
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      // `amount` (fraction of the element visible) instead of a fixed pixel
      // margin: a flat "-100px" band creates a dead zone near the viewport
      // edges that a short mobile section can land entirely inside (e.g. a
      // title scrolled to sit just under the fixed nav), so the observer
      // never reports an intersection and — since `once: true` — the text
      // is stuck permanently at its hidden/invisible state.
      viewport={{ once: true, amount: 0.4 }}
      aria-label={text}
    >
      {words.map(({ word, key, wordIdx }) => (
        <span key={key} className="inline-block whitespace-nowrap" aria-hidden="true">
          {byWord ? (
            <motion.span variants={itemVariants} className="inline-block">
              {word}
            </motion.span>
          ) : (
            word.split("").map((char, charIdx) => (
              <motion.span
                // biome-ignore lint/suspicious/noArrayIndexKey: chars are positional and static
                key={`${char}-${charIdx}`}
                variants={itemVariants}
                className="inline-block"
              >
                {char}
              </motion.span>
            ))
          )}
          {wordIdx < words.length - 1 ? "\u00A0" : null}
        </span>
      ))}
    </motion.span>
  );
};

export default WaveText;
